import { useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { useColorMode } from '@chakra-ui/react';
import { useBettieSettingInfo, type BettieSettingInfo } from '../../hooks/useBettieSettingInfo';
import { parseMemberCalledMessage } from '../../../utils/bettieUtils';
import PulsingGradientCircle from './PulsingGradientCircle';
import SpeechBallon from './SpeechBallon';
import { Button } from '@peacecover/component';
import type { components } from '../../../types/schema';

type ApiV1MemberPersona = components['schemas']['ApiV1MemberPersona'];

interface BettieSettingCompleteProps {
  bettiePersona: ApiV1MemberPersona;
  bettieSettingInfo: BettieSettingInfo;
  chatRoomUuid: string;
}

const BettieSettingComplete = ({
  bettiePersona,
  bettieSettingInfo,
  chatRoomUuid
}: BettieSettingCompleteProps) => {
  const { colorMode } = useColorMode();
  const navigate = useNavigate();
  const { resetBettieSettingInfo } = useBettieSettingInfo();
  const handleEnterChatRoom = () => {
    navigate(`/bettie-chats/${chatRoomUuid}`, {
      replace: true
    });
    resetBettieSettingInfo();
  };

  return (
    <div
      className="min-h-screen pt-[calc(env(safe-area-inset-top)+55px)] pb-[env(safe-area-inset-bottom)] fixed inset-0 flex flex-col items-center"
      style={{
        backgroundImage: `url(/assets/bettie/bg_bettie_complete_${colorMode}.png)`,
        backgroundRepeat: 'round'
      }}
    >
      <div className="absolute top-[calc(54px+env(safe-area-inset-top))] left-0 flex flex-col items-start gap-[14px] px-[24px] py-[10px]">
        <h1 className="text-[26px] font-bold text-text-main-lv1 leading-[31px] whitespace-pre-line">
          {t('BettieSetting.settingComplete.title')}
        </h1>
        <p className="text-[15px] font-medium text-text-main-lv4 leading-[18px]">
          {t('BettieSetting.settingComplete.description')}
        </p>
      </div>
      <div className="flex flex-col items-center mt-[220px]">
        <div className="mb-[30px] z-10">
          <SpeechBallon
            text={parseMemberCalledMessage(bettiePersona?.conversationStartMessage || '', {
              fan_name: bettieSettingInfo.userNickname,
              member_name: bettieSettingInfo.bettieNickname
            })}
          />
        </div>
        <PulsingGradientCircle
          gradientCircleSize="153px"
          glassCircleSize="149px"
          showDottedCircle={false}
          blur="9.5px"
        >
          <img
            src={bettieSettingInfo.dataUrl as string}
            alt={bettieSettingInfo.bettieNickname}
            className="w-[138px] h-[138px] rounded-full object-cover"
          />
        </PulsingGradientCircle>
        <p className="text-[16px] font-semibold text-text-main-lv2 leading-[19px] mt-[5px] z-10">
          {bettieSettingInfo.bettieNickname}
        </p>
      </div>
      <div className="flex flex-col gap-[32px] items-center fixed bottom-[calc(10px+env(safe-area-inset-bottom))] w-full px-[24px]">
        <p className="text-[13px] font-medium text-text-main-lv2 leading-[16px]">
          {t('BettieSetting.settingComplete.goToChatRoom')}
        </p>
        <Button
          size="large"
          className="w-full text-[17px] font-semibold"
          onClick={handleEnterChatRoom}
        >
          {t('BettieSetting.settingComplete.startButton')}
        </Button>
      </div>
    </div>
  );
};

export default BettieSettingComplete;
